import React from 'react';
import { Popover, PopoverTrigger, PopoverContent, Button, Input, Spinner } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useHistory } from 'react-router-dom';
import useProductSearch from '../hooks/use-product-search';

interface SearchPopoverProps {
  onNavigate?: (path: string) => void;
  variant?: 'popover' | 'inline';
  className?: string;
  autoFocusInline?: boolean;
  forceInlineOpen?: boolean;
  onInlineDismiss?: () => void;
}

const MIN_QUERY_LENGTH = 2;

export const SearchPopover: React.FC<SearchPopoverProps> = ({
  onNavigate,
  variant = 'popover',
  className,
  autoFocusInline = false,
  forceInlineOpen,
  onInlineDismiss
}) => {
  const isInline = variant === 'inline';
  const shouldForceInlineOpen = isInline && forceInlineOpen === true;
  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const [hasInteracted, setHasInteracted] = React.useState(!isInline);
  const [inlineResultsOpen, setInlineResultsOpen] = React.useState(false);
  const [inlineDismissed, setInlineDismissed] = React.useState(false);
  const inlineContainerRef = React.useRef<HTMLDivElement | null>(null);
  const history = useHistory();

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);
    return () => clearTimeout(timeout);
  }, [query]);

  const { results, isLoading, error, canSearch } = useProductSearch({
    query: debouncedQuery,
    limit: 8,
    includeFacets: false,
    enabled: debouncedQuery.length >= MIN_QUERY_LENGTH
  });

  React.useEffect(() => {
    if (!isInline) return;
    if (forceInlineOpen === undefined) return;
    if (forceInlineOpen) {
      setInlineDismissed(false);
      if (!hasInteracted) {
        setHasInteracted(true);
      }
    } else {
      setInlineResultsOpen(false);
    }
  }, [forceInlineOpen, isInline, hasInteracted]);

  React.useEffect(() => {
    if (!isInline) return;
    const trimmed = query.trim();
    const baseCondition = hasInteracted && (trimmed.length > 0 || isLoading || !!error);
    const shouldOpen = (!inlineDismissed && baseCondition) || shouldForceInlineOpen;
    setInlineResultsOpen(shouldOpen);
  }, [isInline, inlineDismissed, hasInteracted, query, isLoading, error, shouldForceInlineOpen]);

  React.useEffect(() => {
    if (!isInline || !inlineResultsOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (inlineContainerRef.current && !inlineContainerRef.current.contains(event.target as Node)) {
        setInlineDismissed(true);
        setInlineResultsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isInline, inlineResultsOpen]);

  const handleNavigate = (path: string) => {
    const navigate = () => {
      if (onNavigate) {
        onNavigate(path);
      } else {
        history.push(path);
      }
    };

    setIsOpen(false);
    setInlineResultsOpen(false);
    setInlineDismissed(false);
    setHasInteracted(!isInline);
    setQuery('');
    setDebouncedQuery('');

    navigate();
  };

  const handleSubmit = () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    handleNavigate(`/search?q=${encodeURIComponent(trimmed)}`);
  };

  const handleInputChange = (value: string) => {
    if (!hasInteracted) {
      setHasInteracted(true);
    }
    if (inlineDismissed) {
      setInlineDismissed(false);
    }
    setQuery(value);
  };

  const handleInputFocus = () => {
    if (!hasInteracted) {
      setHasInteracted(true);
    }
    if (inlineDismissed) {
      setInlineDismissed(false);
    }
  };

  const renderResults = () => {
    if (!query.trim()) {
      return <p className="text-sm text-foreground/60">Введите запрос, чтобы начать поиск.</p>;
    }

    if (query.trim().length < MIN_QUERY_LENGTH) {
      return <p className="text-sm text-foreground/60">Введите минимум {MIN_QUERY_LENGTH} символа.</p>;
    }

    if (isLoading) {
      return (
        <div className="flex items-center gap-3 text-sm text-foreground/70">
          <Spinner size="sm" />
          Поиск...
        </div>
      );
    }

    if (error) {
      return <p className="text-sm text-danger">{error.message}</p>;
    }

    if (!results.length) {
      return <p className="text-sm text-foreground/60">Ничего не найдено. Попробуйте другое слово.</p>;
    }

    return (
      <ul className="max-h-80 overflow-y-auto divide-y divide-divider">
        {results.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className="w-full flex items-center gap-3 p-3 text-left hover:bg-content2/60 transition"
              onClick={() => handleNavigate(`/product-detail/${item.slug || item.id}`)}
            >
              <div className="w-12 h-12 bg-content2/80 flex items-center justify-center overflow-hidden">
                {item.image ? (
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[10px] uppercase tracking-wide text-foreground/50">Нет фото</span>
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{item.name}</p>
                <div className="text-xs text-foreground/60 flex flex-wrap gap-2">
                  {item.category?.name && <span>{item.category.name}</span>}
                  {item.article && <span>Арт. {item.article}</span>}
                </div>
                {item.matches && (
                  <div className="mt-1 flex flex-wrap gap-1 text-[11px] uppercase tracking-wide">
                    {item.matches.name && <span className="px-1.5 py-0.5 bg-primary/10 text-primary">Название</span>}
                    {item.matches.article && <span className="px-1.5 py-0.5 bg-primary/10 text-primary">Артикул</span>}
                    {item.matches.aroma && item.matches.aroma.length > 0 && (
                      <span className="px-1.5 py-0.5 bg-primary/10 text-primary">
                        Аромат: {item.matches.aroma.slice(0, 2).join(', ')}
                        {item.matches.aroma.length > 2 ? '…' : ''}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </button>
          </li>
        ))}
      </ul>
    );
  };

  const inputField = (
    <Input
      autoFocus={isInline ? autoFocusInline : !isInline}
      variant={isInline ? 'flat' : 'bordered'}
      placeholder="Поиск по ароматам, названиям, артикулам"
      value={query}
      onValueChange={handleInputChange}
      onFocus={handleInputFocus}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          handleSubmit();
        }
      }}
      startContent={isInline ? <Icon icon="lucide:search" width={18} className="text-foreground/60" /> : undefined}
      endContent={query && (
        <button
          type="button"
          className="text-foreground/60"
          onClick={() => {
            setQuery('');
            setDebouncedQuery('');
            setInlineResultsOpen(false);
            setInlineDismissed(false);
            setHasInteracted(isInline ? false : hasInteracted);
          }}
        >
          <Icon icon="lucide:x" width={16} />
        </button>
      )}
      classNames={isInline ? {
        base: 'outline-none shadow-none data-[focus-visible=true]:outline-none data-[focus-visible=true]:shadow-none',
        mainWrapper: 'outline-none shadow-none data-[focus-visible=true]:outline-none data-[focus-visible=true]:shadow-none',
        inputWrapper: 'bg-content1/80 border border-divider/60 rounded-none px-3 h-10 outline-none focus:outline-none focus-visible:outline-none focus-within:outline-none focus-within:!shadow-none data-[focus-within=true]:outline-none data-[focus-within=true]:shadow-none data-[focus-within=true]:border-divider/60 data-[focus=true]:border-divider/60 data-[focus=true]:shadow-none data-[focus=true]:ring-0 data-[focus-visible=true]:ring-0 data-[focus-visible=true]:shadow-none data-[focus-visible=true]:outline-none',
        input: 'text-[13px] outline-none focus:outline-none focus-visible:outline-none'
      } : { inputWrapper: 'rounded-none' }}
    />
  );

  if (isInline) {
    return (
      <div
        ref={inlineContainerRef}
        className={['relative w-full', className].filter(Boolean).join(' ')}
        onBlurCapture={(event) => {
          if (event.currentTarget.contains(event.relatedTarget as Node)) return;
          setInlineResultsOpen(false);
          setInlineDismissed(true);
          onInlineDismiss?.();
        }}
        onKeyDownCapture={(event) => {
          if (event.key === 'Escape') {
            setInlineResultsOpen(false);
            setInlineDismissed(true);
            onInlineDismiss?.();
          }
        }}
      >
        {inputField}
        {inlineResultsOpen && (
          <div className="absolute left-0 right-0 mt-3 border border-divider/60 bg-background/95 p-4 space-y-3 shadow-2xl z-20 rounded-none">
            <div className="text-xs text-foreground/60 uppercase tracking-[0.3em]">Результаты</div>
            {renderResults()}
            {canSearch && (
              <Button
                variant="flat"
                color="primary"
                className="w-full"
                onPress={handleSubmit}
              >
                Показать все результаты
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-end"
      offset={12}
      classNames={{ content: 'p-0 border border-divider/60 bg-background/95 w-[420px] max-w-[95vw] rounded-none' }}
    >
      <PopoverTrigger>
        <Button isIconOnly variant="light" aria-label="Поиск" className="text-foreground/80">
          <Icon icon="lucide:search" width={18} />
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <div className="p-4 space-y-3">
          {inputField}
          <div className="text-xs text-foreground/60 uppercase tracking-[0.3em]">Результаты</div>
          {renderResults()}
          {canSearch && (
            <Button
              variant="light"
              color="primary"
              className="w-full"
              onPress={handleSubmit}
            >
              Показать все результаты
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SearchPopover;
